import React from 'react';
import { AlertCircle, ArrowUp } from 'lucide-react';

import { Button } from './ui/button';
import { CommandItem } from './ui/command';
import { Spinner } from './Spinner';
import { formatNumber } from '../lib/formatters';
import { LIST_BATCH_SIZE, LIST_ROW_CAP, type ListStatus } from '../hooks/useInfiniteList';
import { UNRETRYABLE_KINDS, describeError } from '../lib/errors';
import { cn } from '../lib/utils';

export interface ListContinuationProps {
  status: ListStatus;
  hasMore: boolean;
  /** `{auto: true}` from the sentinel; a click or keyboard choice passes nothing. */
  onLoadMore?: (options?: { auto?: boolean }) => void;
  onRetryBatch?: () => void;
  onReset?: () => void;
  /**
   * The failed batch's error. Its kind decides Retry (ruling 19): a forbidden,
   * not_found or unauthenticated refusal gets the cause sentence and no Retry,
   * because repeating the request can never succeed.
   */
  error?: unknown;
  /** Plural noun: 'sensors', 'event types'. */
  itemNoun: string;
  loadedCount: number;
  totalCount?: number | null;
  /**
   * The scrolling element the sentinel is measured against. null (or a ref
   * holding null) means the window.
   */
  rootRef?: React.RefObject<HTMLElement | null>;
  /** Scrolls the root to the top and moves focus to the list's search or the page h1. */
  onBackToTop?: () => void;
  /**
   * 'table' and 'list' render buttons; 'command' renders the keyboard
   * fallback as a CommandItem inside a cmdk CommandList.
   */
  variant?: 'table' | 'list' | 'command';
  className?: string;
  style?: React.CSSProperties;
}

const USER_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

/**
 * The trailing controls of every list — after the rows, never among them.
 *
 * - more rows exist: a sentinel that loads the next batch as it nears view,
 *   plus a real 'Load more {itemNoun}' button that is always present (focus is
 *   never stranded) and only aria-disabled while a batch is in flight
 * - a batch failed: "Couldn't load more {itemNoun}." + Retry (same batch); for a
 *   forbidden, not_found or unauthenticated refusal, the cause and no Retry
 * - the batches stopped adding rows: 'The list changed while loading.' + Reload from top
 * - the row cap: the narrowing sentence, and loading stops
 * - the end, after more than one batch: 'All {n} {itemNoun} loaded' + Back to top
 *
 * Auto-loading is bounded: a sentinel intersection counts as automatic until
 * the user scrolls, wheels, touches or presses a key, and the hook refuses
 * more than LIST_AUTO_FILL_MAX automatic loads in a row.
 */
export const ListContinuation: React.FC<ListContinuationProps> = ({
  status,
  hasMore,
  onLoadMore,
  onRetryBatch,
  onReset,
  error,
  itemNoun,
  loadedCount,
  totalCount,
  rootRef,
  onBackToTop,
  variant = 'list',
  className,
  style,
}) => {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const userActedRef = React.useRef(false);
  const onLoadMoreRef = React.useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const root = rootRef?.current ?? null;

  // Any user input since the last sentinel load makes the next one user-driven.
  React.useEffect(() => {
    const target: HTMLElement | Window = root ?? window;
    const mark = () => {
      userActedRef.current = true;
    };
    const events = [...USER_INPUT_EVENTS, 'scroll'] as const;
    for (const name of events) target.addEventListener(name, mark, { passive: true });
    return () => {
      for (const name of events) target.removeEventListener(name, mark);
    };
  }, [root]);

  const observing = status === 'ready' && hasMore && Boolean(onLoadMore);
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!observing || !sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        const userActed = userActedRef.current;
        userActedRef.current = false;
        onLoadMoreRef.current?.(userActed ? undefined : { auto: true });
      },
      { root, rootMargin: '0px 0px 400px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // loadedCount: re-observe after each batch so a sentinel still in view fires again.
  }, [observing, root, loadedCount]);

  const loading = status === 'loadingMore';
  const noun = itemNoun;

  const sentinel = <div ref={sentinelRef} aria-hidden="true" data-list-sentinel="" className="h-px" />;

  let body: React.ReactNode = null;
  if (status === 'appendError') {
    const described = error == null ? null : describeError(error);
    const retryable = !described || !UNRETRYABLE_KINDS.includes(described.kind);
    const cause = described && !retryable ? ` ${described.message}` : '';
    body = variant === 'command' ? (
      retryable ? (
        <CommandItem value={`__retry-${noun}`} onSelect={() => onRetryBatch?.()}>
          <AlertCircle aria-hidden="true" className="text-destructive" />
          {`Couldn't load more ${noun}. Retry`}
        </CommandItem>
      ) : (
        <CommandItem value={`__failed-${noun}`} disabled>
          <AlertCircle aria-hidden="true" className="text-destructive" />
          {`Couldn't load more ${noun}.${cause}`}
        </CommandItem>
      )
    ) : (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <AlertCircle aria-hidden="true" className="h-4 w-4 text-destructive" />
        <span>{`Couldn't load more ${noun}.${cause}`}</span>
        {retryable ? (
          <Button type="button" variant="outline" size="sm" onClick={() => onRetryBatch?.()}>
            Retry
          </Button>
        ) : null}
      </div>
    );
  } else if (status === 'stalled') {
    body = variant === 'command' ? (
      <CommandItem value={`__reload-${noun}`} onSelect={() => onReset?.()}>
        The list changed while loading. Reload from top
      </CommandItem>
    ) : (
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>The list changed while loading.</span>
        <Button type="button" variant="outline" size="sm" onClick={() => onReset?.()}>
          Reload from top
        </Button>
      </div>
    );
  } else if (status === 'capped') {
    body = (
      <p className="px-2 text-sm text-muted-foreground">
        {`Showing the first ${formatNumber(LIST_ROW_CAP)}${
          typeof totalCount === 'number' ? ` of ${formatNumber(totalCount)}` : ''
        } ${noun}. Search, filter or reverse the sort to see others.`}
      </p>
    );
  } else if (hasMore && (status === 'ready' || status === 'loadingMore')) {
    const request = () => {
      if (!loading) onLoadMore?.();
    };
    body = variant === 'command' ? (
      <CommandItem value={`__load-more-${noun}`} onSelect={request} aria-disabled={loading || undefined}>
        {loading ? `Loading more ${noun}…` : `Load more ${noun}`}
      </CommandItem>
    ) : (
      <div className="flex flex-wrap items-center gap-2" aria-busy={loading || undefined}>
        {loading ? <Spinner size="sm" label={`Loading more ${noun}…`} /> : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-disabled={loading || undefined}
          onClick={request}
        >
          {`Load more ${noun}`}
        </Button>
      </div>
    );
  } else if (!hasMore && status === 'ready' && loadedCount > LIST_BATCH_SIZE && variant !== 'command') {
    body = (
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>{`All ${formatNumber(loadedCount)} ${noun} loaded`}</span>
        {onBackToTop ? (
          <Button type="button" variant="ghost" size="sm" onClick={onBackToTop}>
            <ArrowUp aria-hidden="true" />
            Back to top
          </Button>
        ) : null}
      </div>
    );
  }

  if (variant === 'command') {
    return (
      <>
        {body}
        {sentinel}
      </>
    );
  }

  return (
    <div
      className={cn(body ? 'px-4 py-3' : '', className)}
      style={style}
      data-list-controls=""
      data-list-state={status}
      data-loaded-count={loadedCount}
    >
      {sentinel}
      {body}
    </div>
  );
};
